import { useState, useEffect } from 'react';
import { setTagsCache } from '../utils/valmislauseStorage';
import {
  getValmislauseTagit, createValmislauseTag, deleteValmislauseTag,
  setValmislauseTagReportTypes, setValmislauseTagSections, getLlmReportTypes,
} from '../api/api';
import { sanitizeTagName } from './ValmislauseFields';
import ValmislauseBrowser from './ValmislauseBrowser';

// This page is ValmislauseBrowser rendered directly as the page body, no
// modal chrome around it — the exact same component the report editor's
// "+ Valmislause" opens, just in "all report types" mode instead of
// "this report's type". One implementation, so a fix or feature built for
// one surface is automatically there on the other; this page's own job is
// just fetching the two things the browser needs (tags, report types) and
// owning tag create/delete (the only actions the browser can't do itself,
// since it has no report context to carry report_types scoping with).
// typeFilter/selectedTag/activeChapter + their setters: the admin page's
// own URL-synced view state (which "Raporttityyppi" tab, which tag, and
// which chapter are open), owned by AllReports.jsx the same way its other
// filters are — this component just forwards them to ValmislauseBrowser as
// its initial/controlled values.
export default function ValmislauseAdmin({ typeFilter, onTypeFilterChange, selectedTag, onSelectedTagChange, activeChapter, onActiveChapterChange }) {
  const [allTags, setAllTags] = useState([]);
  const [allReportTypes, setAllReportTypes] = useState([]);

  useEffect(() => {
    getValmislauseTagit().then(r => {
      const t = r.data ?? [];
      setAllTags(t);
      setTagsCache(t);
    });
    getLlmReportTypes().then(r => setAllReportTypes(r.data ?? [])).catch(() => setAllReportTypes([]));
  }, []);

  // reportTypes: [] means universal; otherwise scoped to whichever type
  // tab was active when the tag was created — same convention as the
  // report editor's own tag creation. sections: set when creating from a
  // chapter's own "+" button.
  const createTag = async (rawName, reportTypes = [], sections = []) => {
    const name = sanitizeTagName(rawName);
    if (!name) return null;
    const existing = allTags.find(t => t.nimi === name);
    if (existing) return existing;
    try {
      const res = await createValmislauseTag(name);
      let tag = res.data;
      if (reportTypes.length) {
        await setValmislauseTagReportTypes(tag.id, reportTypes);
        tag = { ...tag, report_types: reportTypes };
      }
      if (sections.length) {
        await setValmislauseTagSections(tag.id, sections);
        tag = { ...tag, sections };
      }
      const updated = [...allTags, tag].sort((a, b) => a.nimi.localeCompare(b.nimi));
      setAllTags(updated);
      setTagsCache(updated);
      return tag;
    } catch {
      return null;
    }
  };

  const deleteTag = async (id) => {
    await deleteValmislauseTag(id);
    const updated = allTags.filter(t => t.id !== id);
    setAllTags(updated);
    setTagsCache(updated);
  };

  const updateTagReportTypes = (id, reportTypes) => {
    const updated = allTags.map(t => t.id === id ? { ...t, report_types: reportTypes } : t);
    setAllTags(updated);
    setTagsCache(updated);
  };

  const updateTagSections = (id, sections) => {
    const updated = allTags.map(t => t.id === id ? { ...t, sections } : t);
    setAllTags(updated);
    setTagsCache(updated);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <ValmislauseBrowser
        typeTabRow="all"
        allTags={allTags}
        allReportTypes={allReportTypes}
        onCreateTag={createTag}
        onDeleteTag={deleteTag}
        onTagReportTypesChange={updateTagReportTypes}
        onTagSectionsChange={updateTagSections}
        initialTypeFilter={typeFilter}
        onTypeFilterChange={onTypeFilterChange}
        initialSelectedTag={selectedTag || undefined}
        onSelectedTagChange={onSelectedTagChange}
        initialActiveChapter={activeChapter || undefined}
        onActiveChapterChange={onActiveChapterChange}
      />
    </div>
  );
}
